// Copyright (c) Docugami, Inc. All rights reserved.

import { ITooltipHostStyles, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { userFontFamily } from "components/common/Fonts";
import { WebDocHostProps } from "components/doc_hosting/doc_hosts/web/presentation/WebDocHost";
import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { UnassignedDocSetId } from "components/file_management/file_helpers";
import { FileWarningsIndicator } from "components/file_management/presentation/FileWarningIndicators";
import { SdvContextProps, withSdvContext } from "components/sdv/SdvContext";
import { greyText, homeGray, primaryThemeSettings } from "components/shell/presentation/AppStyles";
import { DocumentViewCanvas } from "components/work_path/DocumentViewCanvas";
import { FileManagerInstance } from "filemanager/FileManager";
import { ClusterDocument, IngestionStatus, IngestionWarnings } from "networking/api";
import path from 'path';
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { ensureDocument } from "store/documents/actions";
import styled from "styled-components";
import { DocumentPreviewVisibilityChangedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { useAssignDocSetCommand } from "./AssignDocSetCommand";
import { useDeleteFilesCommand } from "./DeleteFilesCommand";
import { useDownloadDgmlCommand } from "./DownloadDgmlCommand";
import { useDownloadFilesCommand } from "./DownloadFilesCommand";
import { useAppDispatch, useAppSelector } from "./redux";

const PreviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    align-items: center;
    height: 100%;
    border-top: 1px solid ${greyText};
    background-color: ${primaryThemeSettings.palette.neutralLight};
`;

const CloseButton = styled.button`
    align-self: flex-end;
    border: none;
    cursor: pointer;
    background-color: transparent;
    font-size: 20px;
    color: ${homeGray};
    margin-top: 5px;
`;

const FileNameContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 0 50px;
    box-sizing: border-box;
`;


const overflowTooltipStyles: ITooltipHostStyles = {
    root: {
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: "center",
        fontFamily: userFontFamily,
        fontWeight: 700,
        fontSize: "30px",
        lineHeight: "36px"
    }
}

export interface FilePreviewProps extends SdvContextProps {
    webDocProps: WebDocHostProps;
    onClose: () => void;
    warnings?: IngestionWarnings;
    onNavigatePrev?: () => void;
    onNavigateNext?: () => void;
}

export const FilePreview = withSdvContext((props: FilePreviewProps): JSX.Element => {
    const webDocProps = props.webDocProps;
    const curDocBase = useSelector((state: AppState) => state.documents.indexed[webDocProps.documentId]?.clusterDocument?.filename ?? '');
    const curDocName = path.basename(curDocBase, path.extname(curDocBase));

    return (
        <PreviewContainer>
            <CloseButton onClick={() => props.onClose()}>{'\u2715'}</CloseButton>
            <FileNameContainer>
                <TooltipHost content={curDocName} overflowMode={TooltipOverflowMode.Self} styles={overflowTooltipStyles}>
                    {curDocName}
                </TooltipHost>
                <FileWarningsIndicator warnings={props.warnings} />
            </FileNameContainer>
            <DocumentViewCanvas documentId={webDocProps.documentId} docSetId={webDocProps.clusterId} isFilePreview showNavigation onNavigateNext={props.onNavigateNext} onNavigatePrev={props.onNavigatePrev} />
        </PreviewContainer>
    );
});

export function useFilePreview(contextDocumentIds: string[] | undefined) {
    const [previewDocument, setPreviewDocument] = useState<ClusterDocument | undefined>();

    const allDocuments = useAppSelector(state => state.documents.indexed);
    const isSampleDocument = useAppSelector(state => !!previewDocument ? !!state.clusters.indexed[previewDocument.clusterId]?.clusterInfo.isDocugamiCluster : undefined);

    const dispatch = useAppDispatch();

    const getDocument = useCallback((documentId: string) => {
        return FileManagerInstance.getFileFromFolderId(documentId)?.properties ?? allDocuments[documentId]?.clusterDocument;
    }, [allDocuments]);

    const canPreview = useCallback((documentId: string) => {
        const document = getDocument(documentId);
        return !!document && document.ingestionStatus === IngestionStatus.Succeeded;
    }, [getDocument]);

    const setPreviewFile = useCallback(async (documentId: string) => {
        if (!canPreview(documentId)) {
            return;
        }

        const document = getDocument(documentId);
        if (!document) {
            return;
        }

        // Ensure document state in redux for the file preview
        const clusterDocument = await dispatch(ensureDocument(document.folderId));
        if (!!clusterDocument) {
            setPreviewDocument(clusterDocument);
            Telemetry.trackEvent<DocumentPreviewVisibilityChangedSchema>({
                visibility: true,
                folderId: document.folderId,
                clusterId: document.clusterId,
                eventName: TelemetryEvent.DocumentPreviewVisibilityChanged
            });
        }
    }, [canPreview, getDocument, dispatch]);

    const closeFilePreview = useCallback(() => {
        setPreviewDocument(undefined);
        Telemetry.trackEvent<DocumentPreviewVisibilityChangedSchema>({
            visibility: false,
            eventName: TelemetryEvent.DocumentPreviewVisibilityChanged
        });
    }, []);

    // Preview document commands
    const commandsFilePaths = useMemo(() => !!previewDocument ? [previewDocument.fileId] : [], [previewDocument]);
    const assignCommandText: string = useMemo(() => !!previewDocument && previewDocument.clusterId === UnassignedDocSetId ? "Assign to" : "Reassign", [previewDocument]);
    const assignCommand = useAssignDocSetCommand(assignCommandText, commandsFilePaths, () => { }, true);
    const downloadCommand = useDownloadFilesCommand(commandsFilePaths, () => { });
    const downloadDgmlCommand = useDownloadDgmlCommand(commandsFilePaths);
    const deleteFilesCommand = useDeleteFilesCommand(commandsFilePaths, closeFilePreview);
    const commands: IDocumentCommand[] = useMemo(() =>
        isSampleDocument ?
            [downloadCommand, downloadDgmlCommand] :
            [assignCommand, downloadCommand, downloadDgmlCommand, deleteFilesCommand],
        [deleteFilesCommand, assignCommand, downloadCommand, downloadDgmlCommand, isSampleDocument]);
    const overlays = useMemo(() => commands.filter(c => c.isActive && !!c.visual).map(x => x.visual) as JSX.Element[], [commands])

    const filePreview = useMemo(() => {
        if (!previewDocument) {
            return null;
        }

        const clusterId = previewDocument.clusterId;
        const ingestionWarnings: IngestionWarnings | undefined = previewDocument.ingestionWarnings;

        const previewProps: WebDocHostProps = {
            documentId: previewDocument.folderId,
            clusterId: clusterId
        }

        let onNavigateNext: (() => void) | undefined = undefined;
        let onNavigatePrev: (() => void) | undefined = undefined;
        if (!!contextDocumentIds) {
            const currentDocIndex = contextDocumentIds.indexOf(previewDocument.folderId);
            if (!!contextDocumentIds.length && currentDocIndex !== undefined && currentDocIndex !== -1) {
                const isValid = (documentId: string) => {
                    const document = getDocument(documentId);
                    return !!document && document.folderId !== previewDocument.folderId && canPreview(documentId);
                }

                const [nextDocId, prevDocId] = getNextAndPrevValidItems(
                    contextDocumentIds,
                    isValid,
                    currentDocIndex
                )

                if (!!nextDocId) {
                    onNavigateNext = () => setPreviewFile(nextDocId)
                }

                if (!!prevDocId) {
                    onNavigatePrev = () => setPreviewFile(prevDocId)
                }
            }
        }

        return (
            <FilePreview
                documentId={previewProps.documentId}
                clusterId={previewProps.clusterId}
                webDocProps={previewProps}
                onClose={closeFilePreview}
                warnings={ingestionWarnings}
                onNavigateNext={onNavigateNext}
                onNavigatePrev={onNavigatePrev} />
        );
    }, [closeFilePreview, previewDocument, canPreview, setPreviewFile, contextDocumentIds, getDocument]);

    return {
        filePreview: filePreview,
        setPreviewFile: setPreviewFile,
        canPreview: canPreview,
        previewCommands: commands,
        previewOverlays: overlays,
        closeFilePreview: closeFilePreview
    }
}

function getNextAndPrevValidItems<T>(arr: T[], isValid: (item: T) => boolean, startIndex: number = 0): [T | undefined, T | undefined] {
    let next: T | undefined;
    let prev: T | undefined;
    for (let i = 1; i <= arr.length; i++) {
        // Get next item index, and check validity
        if (next === undefined) {
            const nextIndex = (startIndex + i) % arr.length;
            if (isValid(arr[nextIndex])) {
                next = arr[nextIndex];
            }
        }

        // Get prev item index, and check validity
        if (prev === undefined) {
            const previousIndex = (startIndex - i + arr.length) % arr.length;
            if (isValid(arr[previousIndex])) {
                prev = arr[previousIndex];
            }
        }

        if (next !== undefined && prev !== undefined) {
            break;
        }
    }

    return [next, prev];
}